import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button, Flex, Loader } from '@percent/lemonade'
import { MonetaryVerificationsDetailsProps } from './MonetaryVerificationsDetails.types'
import { MonetaryVerificationsTopBar } from './monetaryVerificationsTopBar/MonetaryVerificationsTopBar'
import {
  Organisation,
  ValidationRequestRejectionReasonCodes,
  VerificationOrganisationDetailsCard
} from '@percent/domain/verifications'
import { ErrorView } from '@percent/admin-dashboard/common/components'
import { getCountryFromAlpha3 } from '@percent/admin-dashboard/common/utility/getCountryFromCountryCode'
import { ChecksSummary } from './checksSummary/ChecksSummary'
import { ContactDetails } from './contactDetails/ContactDetails'

export function MonetaryVerificationsDetails({
  monetaryVerification,
  organisation,
  validationSubmission,
  errorMessage,
  isLoading
}: Readonly<MonetaryVerificationsDetailsProps>) {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [activeTab, setActiveTab] = useState('')

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const goToOrganisationPage = () => navigate(`/organizations/${organisation?.id}`)

  const hasNoEligibleOrganisationType =
    !!organisation &&
    validationSubmission?.validationRequest?.rejectionReasonCode ===
      ValidationRequestRejectionReasonCodes.NotEligible &&
    !validationSubmission.configuration?.allowedOrganisations?.types?.some(
      item => organisation?.types?.[item] === 'yes'
    )

  const organisationDetails = organisation ?? monetaryVerification?.organisationDetails

  return (
    <Flex direction="column" gap={16}>
      {monetaryVerification && (
        <MonetaryVerificationsTopBar
          id={monetaryVerification.id}
          organizationName={organisationDetails?.name}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          status={monetaryVerification.status}
        />
      )}
      {monetaryVerification?.checks && (
        <ChecksSummary
          checks={monetaryVerification.checks}
          agentVerificationId={validationSubmission?.id ?? ''}
          organisationName={organisation?.name}
          partnerName={monetaryVerification.partnerName ?? undefined}
          monetaryVerification={monetaryVerification}
          validationSubmission={validationSubmission}
        />
      )}
      {organisationDetails && (
        <VerificationOrganisationDetailsCard
          organisation={organisationDetails as Organisation}
          isLoading={isLoading}
          validationRequest={validationSubmission?.validationRequest}
          eligibility={validationSubmission?.eligibility}
          hasNoEligibleOrganisationType={hasNoEligibleOrganisationType}
          getCountryNameByCode={getCountryFromAlpha3}
          action={
            organisationDetails?.id && (
              <Button variant="secondary" onClick={goToOrganisationPage}>
                {t('typography.viewOrganizationDetails')}
              </Button>
            )
          }
        />
      )}
      <ContactDetails contactDetails={monetaryVerification?.contactDetails ?? []} />
    </Flex>
  )
}
